import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  Animated,
  Dimensions,
} from 'react-native';
import { Audio } from 'expo-av';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

const { width: screenWidth } = Dimensions.get('window');

interface VoiceRecorderProps {
  onRecordingComplete: (audioUri: string, duration: number) => void;
  onRecordingCancel: () => void;
  maxDuration?: number; // Maximum recording duration in seconds
}

const isWeb = Platform.OS === 'web';

export const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  onRecordingCancel,
  maxDuration = 120, // 2 minutes max
}) => {
  const styles = useThemedStyles(getStyles);
  const [recording, setRecording] = useState<Audio.Recording | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  
  const durationInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const waveAnim = useRef(new Animated.Value(0)).current;
  const mediaRecorderRef = useRef<any>(null);
  const mediaStreamRef = useRef<any>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const webMimeTypeRef = useRef('audio/ogg');
  const recordingDurationRef = useRef(0);

  useEffect(() => {
    // Request audio recording permission
    requestAudioPermission();
    
    // Cleanup on unmount
    return () => {
      if (durationInterval.current) {
        clearInterval(durationInterval.current);
      }
      if (recording) {
        recording.stopAndUnloadAsync();
      }
      stopWebRecordingInfrastructure();
    };
  }, []);

  useEffect(() => {
    recordingDurationRef.current = recordingDuration;
  }, [recordingDuration]);

  const requestAudioPermission = async () => {
    try {
      if (isWeb) {
        const canRecord = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
        setHasPermission(canRecord);
        if (!canRecord) {
          Alert.alert(
            'Recording Not Supported',
            'This browser does not support microphone recording.',
            [{ text: 'OK' }]
          );
        }
        return;
      }

      const { status } = await Audio.requestPermissionsAsync();
      setHasPermission(status === 'granted');
      
      if (status !== 'granted') {
        Alert.alert(
          'Permission Required',
          'Please grant microphone permission to record voice notes.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Failed to request audio permission:', error);
      setHasPermission(false);
    }
  };

  const stopWebRecordingInfrastructure = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track: any) => track.stop());
      mediaStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    recordedChunksRef.current = [];
  };

  const startDurationTimer = () => {
    if (durationInterval.current) {
      clearInterval(durationInterval.current);
    }

    durationInterval.current = setInterval(() => {
      setRecordingDuration(prev => {
        const newDuration = prev + 0.1;
        if (newDuration >= maxDuration) {
          stopRecording();
          return maxDuration;
        }
        return newDuration;
      });
    }, 100);
  };

  const stopDurationTimer = () => {
    if (durationInterval.current) {
      clearInterval(durationInterval.current);
      durationInterval.current = null;
    }
  };

  const resetRecorderState = () => {
    setRecording(null);
    setIsRecording(false);
    setIsPaused(false);
    setRecordingDuration(0);
    stopPulseAnimation();
    stopDurationTimer();
  };

  const getSupportedWebMimeType = () => {
    if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
      return '';
    }

    const candidates = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
    return candidates.find(type => MediaRecorder.isTypeSupported(type)) || '';
  };

  const startRecordingWeb = async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      Alert.alert(
        'Recording Unsupported',
        'Microphone recording is not supported in this browser.',
        [{ text: 'OK' }]
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      recordedChunksRef.current = [];

      const mimeType = getSupportedWebMimeType();
      webMimeTypeRef.current = mimeType || 'audio/ogg';

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: any) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      setIsPaused(false);
      startDurationTimer();
      startPulseAnimation();
    } catch (error) {
      console.error('Failed to start web recording:', error);
      Alert.alert(
        'Recording Error',
        'Failed to access the microphone. Please check browser permissions.',
        [{ text: 'OK' }]
      );
      throw error;
    }
  };

  const stopRecordingWeb = async (emitResult = true) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      return;
    }

    return new Promise<void>((resolve, reject) => {
      recorder.onstop = () => {
        try {
          stopDurationTimer();
          stopPulseAnimation();
          const duration = recordingDurationRef.current;

          const chunks = recordedChunksRef.current.slice();
          recordedChunksRef.current = [];
          stopWebRecordingInfrastructure();

          if (!emitResult) {
            resolve();
            return;
          }

          if (!chunks.length || duration <= 0.5) {
            Alert.alert(
              'Recording Too Short',
              'Please record for at least 1 second.',
              [{ text: 'OK' }]
            );
            onRecordingCancel();
            resolve();
            return;
          }

          const normalizedMime = (webMimeTypeRef.current || 'audio/ogg').split(';')[0];
          const blob = new Blob(chunks, { type: normalizedMime });
          const uri = URL.createObjectURL(blob);
          const taggedUri = `${uri}#mime=${encodeURIComponent(normalizedMime)}`;
          onRecordingComplete(taggedUri, duration);
          resolve();
        } catch (error) {
          console.error('Failed to finalize web recording:', error);
          reject(error);
        }
      };

      recorder.onerror = (event: any) => {
        console.error('MediaRecorder error:', event.error);
        recordedChunksRef.current = [];
        stopWebRecordingInfrastructure();
        reject(event.error);
      };

      recorder.stop();
    });
  };

  const pauseRecordingWeb = async () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording' && typeof recorder.pause === 'function') {
      recorder.pause();
      setIsPaused(true);
      stopPulseAnimation();
      stopDurationTimer();
    }
  };

  const resumeRecordingWeb = async () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'paused' && typeof recorder.resume === 'function') {
      recorder.resume();
      setIsPaused(false);
      startDurationTimer();
      startPulseAnimation();
    }
  };

  const startRecording = async () => {
    try {
      if (hasPermission === false) {
        Alert.alert(
          'Permission Denied',
          'Microphone permission is required to record voice notes.',
          [{ text: 'OK' }]
        );
        return;
      }

      if (isWeb) {
        await startRecordingWeb();
        return;
      }

      // Configure audio mode for recording
      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
        shouldDuckAndroid: true,
        playThroughEarpieceAndroid: false,
        staysActiveInBackground: false,
      });

      // Start recording with WhatsApp-compatible format (OGG)
      // OGG is the preferred format for WhatsApp voice messages
      const { recording: newRecording } = await Audio.Recording.createAsync(
        {
          android: {
            extension: '.m4a',
            outputFormat: Audio.AndroidOutputFormat.MPEG_4,
            audioEncoder: Audio.AndroidAudioEncoder.AAC,
            sampleRate: 44100,
            numberOfChannels: 1,
            bitRate: 128000,
          },
          ios: {
            extension: '.m4a',
            audioQuality: Audio.IOSAudioQuality.HIGH,
            sampleRate: 44100,
            numberOfChannels: 1,
            bitRate: 128000,
            linearPCMBitDepth: 16,
            linearPCMIsBigEndian: false,
            linearPCMIsFloat: false,
            outputFormat: Audio.IOSOutputFormat.MPEG4AAC,
          },
          web: {
            mimeType: 'audio/ogg',
            bitsPerSecond: 128000,
          },
        },
        undefined, // No callback for progress
        100 // Update interval in ms
      );

      setRecording(newRecording);
      setIsRecording(true);
      setRecordingDuration(0);
      setIsPaused(false);

      // Start duration timer
      startDurationTimer();

      // Start pulse animation
      startPulseAnimation();

    } catch (error) {
      console.error('Failed to start recording:', error);
      Alert.alert(
        'Recording Error',
        'Failed to start recording. Please try again.',
        [{ text: 'OK' }]
      );
    }
  };

  const pauseRecording = async () => {
    try {
      if (recording && isRecording) {
        if (isWeb) {
          await pauseRecordingWeb();
          return;
        }

        await recording.pauseAsync();
        setIsPaused(true);
        stopPulseAnimation();
        
        if (durationInterval.current) {
          clearInterval(durationInterval.current);
        }
      }
    } catch (error) {
      console.error('Failed to pause recording:', error);
    }
  };

  const resumeRecording = async () => {
    try {
      if (isPaused) {
        if (isWeb) {
          await resumeRecordingWeb();
          return;
        }

        if (!recording) {
          return;
        }

        await recording.startAsync();
        setIsPaused(false);
        
        // Resume duration timer
        startDurationTimer();

        // Resume pulse animation
        startPulseAnimation();
      }
    } catch (error) {
      console.error('Failed to resume recording:', error);
    }
  };

  const stopRecording = async () => {
    try {
      if (isWeb) {
        await stopRecordingWeb(true);
      } else if (recording) {
        await recording.stopAndUnloadAsync();
        const uri = recording.getURI();
        
        if (uri && recordingDuration > 0.5) { // Minimum 0.5 seconds
          onRecordingComplete(uri, recordingDuration);
        } else {
          Alert.alert(
            'Recording Too Short',
            'Please record for at least 1 second.',
            [{ text: 'OK' }]
          );
          onRecordingCancel();
        }
      }
      
      resetRecorderState();
    } catch (error) {
      console.error('Failed to stop recording:', error);
      onRecordingCancel();
    }
  };

  const cancelRecording = async () => {
    try {
      if (isWeb) {
        await stopRecordingWeb(false);
      } else if (recording) {
        await recording.stopAndUnloadAsync();
      }
      
      resetRecorderState();
      onRecordingCancel();
    } catch (error) {
      console.error('Failed to cancel recording:', error);
      onRecordingCancel();
    }
  };

  const startPulseAnimation = () => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.3,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    ).start();
  };

  const stopPulseAnimation = () => {
    pulseAnim.stopAnimation();
    pulseAnim.setValue(1);
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgress = (): number => {
    return Math.min((recordingDuration / maxDuration) * 100, 100);
  };

  if (hasPermission === null) {
    return (
      <View style={styles.container}>
        <View style={styles.permissionContainer}>
          <Text style={styles.permissionText}>Requesting microphone permission...</Text>
        </View>
      </View>
    );
  }

  if (hasPermission === false) {
    return (
      <View style={styles.container}>
        <View style={styles.permissionContainer}>
          <Ionicons name="mic-off" size={48} color={colors.error} />
          <Text style={styles.permissionText}>Microphone permission required</Text>
          <TouchableOpacity style={styles.permissionButton} onPress={requestAudioPermission}>
            <Text style={styles.permissionButtonText}>Grant Permission</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Recording Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Voice Message</Text>
        <TouchableOpacity style={styles.cancelButton} onPress={cancelRecording}>
          <Ionicons name="close" size={24} color={colors.textDark} />
        </TouchableOpacity>
      </View>

      {/* Recording Status */}
      <View style={styles.statusContainer}>
        <Animated.View style={[styles.recordingIndicator, { transform: [{ scale: pulseAnim }] }]}>
          <Ionicons 
            name={isRecording ? "mic" : isPaused ? "pause" : "mic-off"} 
            size={32} 
            color={colors.white} 
          />
        </Animated.View>
        
        <Text style={styles.statusText}>
          {isRecording ? 'Recording...' : isPaused ? 'Paused' : 'Ready to record'}
        </Text>
        
        <Text style={styles.durationText}>
          {formatDuration(recordingDuration)}
        </Text>
      </View>

      {/* Progress Bar */}
      <View style={styles.progressContainer}>
        <View style={styles.progressBar}>
          <View 
            style={[
              styles.progressFill, 
              { width: `${getProgress()}%` }
            ]} 
          />
        </View>
        <Text style={styles.maxDurationText}>Max: {formatDuration(maxDuration)}</Text>
      </View>

      {/* Control Buttons */}
      <View style={styles.controlsContainer}>
        {!isRecording && !isPaused ? (
          // Start recording button
          <TouchableOpacity 
            style={[styles.controlButton, styles.startButton]} 
            onPress={startRecording}
            activeOpacity={0.8}
          >
            <Ionicons name="mic" size={32} color={colors.white} />
            <Text style={styles.controlButtonText}>Tap to Record</Text>
          </TouchableOpacity>
        ) : (
          // Recording controls
          <View style={styles.recordingControls}>
            <TouchableOpacity 
              style={[styles.controlButton, styles.secondaryButton]} 
              onPress={cancelRecording}
              activeOpacity={0.8}
            >
              <Ionicons name="trash" size={24} color={colors.error} />
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.controlButton, styles.pauseButton]} 
              onPress={isPaused ? resumeRecording : pauseRecording}
              activeOpacity={0.8}
            >
              <Ionicons 
                name={isPaused ? "play" : "pause"} 
                size={28} 
                color={isPaused ? colors.primary : colors.white} 
              />
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.controlButton, styles.sendButton]} 
              onPress={stopRecording}
              activeOpacity={0.8}
            >
              <Ionicons name="checkmark" size={24} color={colors.white} />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Instructions */}
      <Text style={styles.instructions}>
        {!isRecording && !isPaused 
          ? 'Tap the microphone to start recording'
          : isPaused 
          ? 'Tap play to resume or checkmark to send'
          : 'Tap pause to pause or checkmark to send'
        }
      </Text>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.xl,
    borderTopRightRadius: designTokens.borderRadius.xl,
    paddingBottom: Platform.OS === 'ios' ? 34 : designTokens.spacing.lg,
    ...designTokens.shadows.xl,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: designTokens.spacing.lg,
    paddingVertical: designTokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.13),
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  cancelButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: withAlpha(colors.lightGray, 0.25),
  },
  statusContainer: {
    alignItems: 'center',
    paddingVertical: designTokens.spacing.xl,
  },
  recordingIndicator: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: designTokens.spacing.md,
    ...designTokens.shadows.lg,
  },
  statusText: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginBottom: designTokens.spacing.xs,
  },
  durationText: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  progressContainer: {
    paddingHorizontal: designTokens.spacing.lg,
    marginBottom: designTokens.spacing.lg,
  },
  progressBar: {
    height: 4,
    backgroundColor: withAlpha(colors.lightGray, 0.25),
    borderRadius: 2,
    overflow: 'hidden',
    marginBottom: designTokens.spacing.xs,
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: 2,
  },
  maxDurationText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'right',
    fontFamily: typography.fontFamily.regular,
  },
  controlsContainer: {
    paddingHorizontal: designTokens.spacing.lg,
    marginBottom: designTokens.spacing.lg,
  },
  controlButton: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: designTokens.borderRadius.full,
    ...designTokens.shadows.md,
  },
  startButton: {
    backgroundColor: colors.primary,
    width: 120,
    height: 120,
    alignSelf: 'center',
  },
  secondaryButton: {
    backgroundColor: colors.background.surface,
    width: 56,
    height: 56,
    borderWidth: 2,
    borderColor: colors.error,
  },
  pauseButton: {
    backgroundColor: colors.warning,
    width: 64,
    height: 64,
  },
  sendButton: {
    backgroundColor: colors.success,
    width: 56,
    height: 56,
  },
  controlButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    marginTop: designTokens.spacing.xs,
  },
  recordingControls: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  instructions: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
    paddingHorizontal: designTokens.spacing.lg,
    fontFamily: typography.fontFamily.regular,
  },
  permissionContainer: {
    alignItems: 'center',
    paddingVertical: designTokens.spacing['2xl'],
    paddingHorizontal: designTokens.spacing.lg,
  },
  permissionText: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    textAlign: 'center',
    marginTop: designTokens.spacing.md,
    marginBottom: designTokens.spacing.lg,
    fontFamily: typography.fontFamily.medium,
  },
  permissionButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: designTokens.spacing.lg,
    paddingVertical: designTokens.spacing.md,
    borderRadius: designTokens.borderRadius.md,
  },
  permissionButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
  },
});
